/**
 * ServiceManager - Handles NPC services (heal, bank, transport, etc.)
 * Extracted from NPCInteractionUI.tsx
 */

import { NPC } from '../../../../shared/NPCData';

export interface ServiceAction {
  type: 'heal' | 'bank' | 'transport' | 'identify' | 'repair' | 'train';
  cost?: number;
  parameters?: Record<string, any>;
}

export interface ServiceResult {
  success: boolean;
  type: string;
  message: string;
  cost?: number;
}

export class ServiceManager {
  private npc: NPC;
  private onServiceUse?: (action: ServiceAction) => void;
  
  constructor(npc: NPC, onServiceUse?: (action: ServiceAction) => void) {
    this.npc = npc;
    this.onServiceUse = onServiceUse;
  }
  
  /**
   * Use heal service
   */
  useHealService(playerHealth: number, maxHealth: number, cost?: number): ServiceResult {
    if (playerHealth >= maxHealth) {
      return {
        success: false,
        type: 'heal',
        message: 'You are already at full health!'
      };
    }
    
    const result: ServiceResult = {
      success: true,
      type: 'heal',
      message: 'You have been fully healed!',
      cost
    };
    
    if (this.onServiceUse) {
      this.onServiceUse({
        type: 'heal',
        cost,
        parameters: { playerHealth, maxHealth }
      });
    }
    
    return result;
  }
  
  /**
   * Use bank service
   */
  useBankService(playerGold: number, cost?: number): ServiceResult {
    const result: ServiceResult = {
      success: true,
      type: 'bank',
      message: 'Bank access granted',
      cost
    };
    
    if (this.onServiceUse) {
      this.onServiceUse({
        type: 'bank',
        cost,
        parameters: { playerGold }
      });
    }
    
    return result;
  }
  
  /**
   * Use transport service
   */
  useTransportService(destinationId: string, cost?: number): ServiceResult {
    const result: ServiceResult = {
      success: true,
      type: 'transport',
      message: `Transporting to ${destinationId}...`,
      cost
    };
    
    if (this.onServiceUse) {
      this.onServiceUse({
        type: 'transport',
        cost,
        parameters: { destinationId }
      });
    }
    
    return result;
  }
  
  /**
   * Use identify service (for unidentified items)
   */
  useIdentifyService(itemId: string, cost?: number): ServiceResult {
    const result: ServiceResult = {
      success: true,
      type: 'identify',
      message: `Item ${itemId} identified!`,
      cost
    };
    
    if (this.onServiceUse) {
      this.onServiceUse({
        type: 'identify',
        cost,
        parameters: { itemId }
      });
    }
    
    return result;
  }
  
  /**
   * Use repair service
   */
  useRepairService(itemId: string, repairCost: number): ServiceResult {
    const result: ServiceResult = {
      success: true,
      type: 'repair',
      message: `Item ${itemId} repaired for ${repairCost} gold`,
      cost: repairCost
    };
    
    if (this.onServiceUse) {
      this.onServiceUse({
        type: 'repair',
        cost: repairCost,
        parameters: { itemId }
      });
    }
    
    return result;
  }

  /** Dispatch a typed trainer intent. The server remains the sole
   * authority for class, level, proximity, and already-learned checks. */
  useTrainAbility(abilityId: string, quotedCost: number): ServiceResult {
    const result: ServiceResult = {
      success: true,
      type: 'train',
      message: `Requesting training for ${abilityId}`,
      cost: quotedCost,
    };
    this.onServiceUse?.({
      type: 'train',
      cost: quotedCost,
      parameters: { npcId: this.npc.id, abilityId },
    });
    return result;
  }
  
  /**
   * Get available services for this NPC
   */
  getAvailableServices(): ServiceAction[] {
    if (!this.npc.services) return [];
    
    return this.npc.services.map(service => {
      if (service.action === 'heal') {
        return { type: 'heal', cost: service.cost };
      } else if (service.action === 'bank') {
        return { type: 'bank', cost: service.cost };
      } else if (service.action === 'transport') {
        return { type: 'transport', cost: service.cost }; 
      } else if (service.action.startsWith('learnAbility:')) {
        return {
          type: 'train',
          cost: service.cost,
          parameters: {
            npcId: this.npc.id,
            abilityId: service.action.slice('learnAbility:'.length),
          },
        };
      }
      return { type: (service.action || 'misc') as any };
    });
  }
  
  /**
   * Calculate service cost (if variable)
   */
  calculateServiceCost(serviceType: string, parameters?: Record<string, any>): number {
    switch (serviceType) {
    case 'heal':
      return 50; // Base heal cost
    case 'repair':
      return parameters?.itemCondition || 10;
    case 'identify':
      return 25;
    case 'transport':
      return 100;
    default:
      return 0;
    }
  }
  
  /**
   * Check if player can afford service
   */
  canAffordService(playerGold: number, cost: number = 0): boolean {
    return playerGold >= cost;
  }
}
